import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExperienceWalletForm from 'components/forms/experienceWallet/ExperienceWalletForm';
import OnboardingSteps from 'components/OnboardingSteps';
import OnboardingNavigationButtons from 'components/OnboardingNavigationButtons';
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import { trackEvent } from 'lib/analytics';
import { EMPTY_EXPERIENCE } from 'constants/experienceDefaults';
import OnboardingContentWrapper from 'components/OnboardingContentWrapper';
import { useQueryClient } from '@tanstack/react-query';

export default function OnboardingWorkExperience() {
  const intl = useIntl();
  const history = useHistory();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const queryClient = useQueryClient();

  // Read cached experience data for pre-population on back navigation
  const cachedExp = queryClient.getQueryData(['/users/me/experience/']);
  const cachedJob = cachedExp?.job_experience?.[0];
  const initialExperience = cachedJob
    ? {
        ...EMPTY_EXPERIENCE,
        type: cachedJob.type || '',
        field: cachedJob.fields?.[0] || null,
        title: cachedJob.title || '',
        skills: cachedJob.skills || [],
        start_date: cachedJob.start_date || null,
        end_date: cachedJob.end_date || null,
        employer_provider: cachedJob.employer_provider || '',
        comments: cachedJob.description || '',
        level: cachedJob.level ?? null,
        is_current: cachedJob.is_current ?? false,
      }
    : EMPTY_EXPERIENCE;

  const [experiences, setExperiences] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAppendItem = (item) => {
    setExperiences([...experiences, item]);
    trackEvent('ONBOARDING_WORK_EXPERIENCE_COMPLETE');
    history.push(`${urlPrefix}/onboarding/location?from=work-experience`);
  };

  const handleBack = () => {
    history.push({
      pathname: `${urlPrefix}/onboarding/eligibility`,
      state: { step: 2 },
    });
  };

  return (
    <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
            {intl.formatMessage({ id: 'experience.form.addWorkExperience' })}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
          <OnboardingSteps activeStep={3} />
        </Box>
      </div>
      <hr className={layoutStyles.separatorBold} />
      <Box className={styles.loadingScreen__cardsContainer}>
        <OnboardingContentWrapper>
          <ExperienceWalletForm
            experience={initialExperience}
            experiences={experiences}
            formScope="work"
            appendItem={handleAppendItem}
            formId="onboarding-work-experience-form"
            onSubmittingChange={setIsSubmitting}
          />
          <OnboardingNavigationButtons
            onBack={handleBack}
            loading={isSubmitting}
            onNext={() => {
              const formElement = document.getElementById('onboarding-work-experience-form');
              if (formElement) {
                formElement.requestSubmit();
              }
            }}
            backId="back-work-experience-button"
            nextId="next-work-experience-button"
          />
        </OnboardingContentWrapper>
      </Box>
    </Box>
  );
}
